// src/data/immigrationGuides/spouseVisaILR.js

const spouseVisaILR = {
    slug: "spouse-visa-ilr",

    title: "Spouse Visa ILR Guide UK: Partner Settlement Requirements",

    seo: {
        metaTitle:
            "Spouse Visa ILR UK Guide: Requirements, Process & Checklist",
        metaDescription:
            "How spouse visa holders apply for ILR in the UK: relationship requirements, residence rules, English requirements, the Life in the UK Test and documents.",
        keywords: [
            "spouse visa ILR",
            "partner visa settlement UK",
            "ILR after spouse visa",
            "family visa ILR"
        ]
    },

    breadcrumbs: [
        {
            name: "Home",
            url: "/"
        },
        {
            name: "Study Guide",
            url: "/study-guide"
        },
        {
            name: "Spouse Visa ILR",
            url: "/spouse-visa-ilr"
        }
    ],

    introduction: `
The spouse visa route allows eligible partners of British citizens or people
settled in the UK to live together in the UK.

People on the family visa route may be able to apply for [Indefinite Leave to
Remain](https://www.gov.uk/indefinite-leave-to-remain-family) (ILR) after meeting the relevant requirements.

The requirements depend on the specific family visa route and the applicant's
circumstances.
`,

    sections: [
        {
            heading: "What is spouse visa ILR?",
            content: `
Spouse visa ILR is settlement status granted to eligible applicants who meet the
requirements of the family visa route.

After obtaining ILR, a person is no longer subject to the time restrictions of
their temporary visa.
`
        },

        {
            heading: "How long does spouse visa ILR take?",
            content: `
Many spouse and partner visa applicants follow a route requiring a qualifying
period before applying for settlement.

Applicants should check whether they are on the correct route and have completed
the required qualifying period.
`
        },

        {
            heading: "Relationship requirement",
            content: `
Applicants must continue to meet the relationship requirements.

This normally involves showing that:

- the relationship continues to exist
- the couple meet the relevant family visa requirements
- the circumstances have not changed in a way that affects eligibility

Evidence requirements depend on individual circumstances.
`
        },

        {
            heading: "Financial requirement",
            content: `
Applicants may need to demonstrate that they meet the financial requirements
applicable to their route.

The requirements can depend on:

- application date
- family circumstances
- immigration rules in force

Applicants should check the latest GOV.UK guidance before applying.
`
        },

        {
            heading: "Residence requirement",
            content: `
Applicants must complete the required qualifying period and meet residence
requirements.

Applicants should review:

- time spent outside the UK
- immigration permission history
- qualifying periods
`
        },

        {
            heading: "English language requirement",
            content: `
Applicants normally need to meet the [English language requirement](https://www.gov.uk/english-language) unless they
qualify for an exemption.

Accepted evidence depends on individual circumstances.
`
        },

        {
            heading: "Life in the UK Test",
            content: `
Most applicants need to pass the [Life in the UK Test](https://www.gov.uk/life-in-the-uk-test) before applying for ILR.

The test covers British:

- history
- government
- law
- traditions
- society
`
        },

        {
            heading: "Application process",
            content: `
The general process includes:

1. Checking eligibility
2. Completing the online application
3. Providing supporting documents
4. Providing biometrics if required
5. Receiving a Home Office decision
`
        },

        {
            heading: "Documents commonly required",
            content: `
Applicants may need:

- passport or identity documents
- evidence of relationship
- financial evidence
- English language evidence
- Life in the UK Test result
- residence evidence

The exact documents depend on individual circumstances.
`
        }
    ],

    checklist: [
        "Confirm spouse visa settlement route",
        "Complete required qualifying period",
        "Check relationship requirements",
        "Review financial requirements",
        "Pass Life in the UK Test",
        "Meet English requirements",
        "Prepare supporting documents"
    ],

    faqs: [
        {
            question: "Can spouse visa holders apply for ILR?",
            answer:
                "Eligible spouse visa holders may apply for ILR after meeting the relevant settlement requirements."
        },
        {
            question: "Do I need the Life in the UK Test for spouse visa ILR?",
            answer:
                "Most applicants need to pass the Life in the UK Test unless exempt."
        },
        {
            question: "Do I need English for spouse visa ILR?",
            answer:
                "Many applicants need to meet English language requirements unless an exemption applies."
        },
        {
            question: "Does my relationship need to continue for ILR?",
            answer:
                "Applicants normally need to continue meeting relationship requirements for the family route."
        }
    ],

    relatedPages: [
        {
            title: "ILR Guide",
            url: "/ilr-guide"
        },
        {
            title: "British Citizenship Guide",
            url: "/british-citizenship-guide"
        },
        {
            title: "Life in the UK Test",
            url: "/mock-exams"
        }
    ],

    officialSources: [
        {
            title: "GOV.UK - Family Visa Settlement",
            url: "https://www.gov.uk/indefinite-leave-to-remain-family"
        },
        {
            title: "GOV.UK - Immigration Rules",
            url: "https://www.gov.uk/guidance/immigration-rules"
        }
    ],

    metadata: {
        contentType: "guide",
        category: "Immigration Guides",
        author: "Life in UK Coach",
        lastUpdated: "2026-07-20"
    }
};

export default spouseVisaILR;